import { useState, useMemo } from "react";

import { cn } from "@/lib/utils";
import { MetaFieldEditModal } from "../../../MetaFieldEditModal";
import { CARD } from "@/config/design-tokens";
import type { ShopifySectionWidgetProps } from "../types";
import { useLanguage } from "@/i18n/useLanguage";
import { getUITranslation, getStatusTranslation } from "@/lib/views/labels/translations";

/**
 * Status Summary Section Widget
 * 
 * Displays order status, delivery status, and fulfillment progress with visual indicators.
 * Apple-style minimal design with progress bars and contextual information.
 */
export function StatusSummarySection({
  section,
  formData,
  onFieldChange,
  disabled,
  onSave,
  isSaving,
  isNew,
}: ShopifySectionWidgetProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const { language } = useLanguage();

  // Hide this section for new orders - status info is not relevant during creation
  if (isNew) {
    return null;
  }

  // Storefront orders show this info in the unified StorefrontOrderBanner
  if (formData.source_channel === "Storefront") return null;
  
  // Translate section label

  // Extract status fields
  const status = formData.status || null;
  const deliveryStatus = formData.delivery_status || null;
  const perDelivered = Number(formData.per_delivered || 0);
  const perBilled = Number(formData.per_billed || 0);
  const perPicked = Number(formData.per_picked || 0);

  // Calculate overall progress (average of all three percentages)
  const overallProgress = useMemo(() => {
    const sum = perDelivered + perBilled + perPicked;
    return sum > 0 ? Math.round(sum / 3) : 0;
  }, [perDelivered, perBilled, perPicked]);

  // Resolve UI labels
  const uiLabels = useMemo(() => {
    const getUI = (key: string, fallback: string) => {
      return getUITranslation(key, language) || fallback;
    };

    return {
      orderStatus: getUI('order_status', 'Order Status'),
      overallProgress: getUI('overall_progress', 'Overall Progress'),
      averageCompletion: getUI('average_completion', 'Average completion'),
      delivered: getUI('delivered', 'Delivered'),
      amountBilled: getUI('amount_billed', 'Amount Billed'),
      picked: getUI('picked', 'Picked'),
      notStarted: getUI('not_started', 'Not Started'),
      fullyBilled: getUI('fully_billed', 'Fully Billed'),
      notBilled: getUI('not_billed', 'Not Billed'),
      fullyPicked: getUI('fully_picked', 'Fully Picked'),
      notPicked: getUI('not_picked', 'Not Picked'),
      partial: getUI('partial', 'Partial'),
      notDelivered: getUI('not_delivered', 'Not Delivered'),
      toDeliver: getUI('to_deliver', 'To Deliver'),
    };
  }, [language]);

  // Translate dynamic status values
  const translatedStatus = useMemo(() => {
    return getStatusTranslation(status, language);
  }, [status, language]);

  const translatedDeliveryStatus = useMemo(() => {
    return getStatusTranslation(deliveryStatus, language);
  }, [deliveryStatus, language]);

  // Format percentage
  const fmtPercent = (val: number) => {
    return val || 0;
  };

  // Get progress label
  const getProgressLabel = (percentage: number, type: "delivered" | "billed" | "picked") => {
    const getUI = (key: string, fallback: string) => {
      return getUITranslation(key, language) || fallback;
    };

    if (percentage === 0) {
      if (type === "delivered") return getUI('no_items_delivered_yet', 'No items delivered yet');
      if (type === "billed") return getUI('no_items_billed_yet', 'No items billed yet');
      if (type === "picked") return getUI('no_items_picked_yet', 'No items picked yet');
    }
    if (percentage === 100) {
      if (type === "delivered") return getUI('all_items_delivered', 'All items delivered');
      if (type === "billed") return getUI('all_items_billed', 'All items billed');
      if (type === "picked") return getUI('all_items_picked', 'All items picked');
    }
    
    // For partial percentages, format with the percentage value
    const template = type === "delivered" 
      ? getUI('percent_of_items_delivered', '{percentage}% of items delivered')
      : type === "billed"
      ? getUI('percent_of_items_billed', '{percentage}% of items billed')
      : getUI('percent_of_items_picked', '{percentage}% of items picked');
    
    return template.replace('{percentage}', percentage.toString());
  };

  return (
    <>
      <div className="space-y-2">
        <div
          className={cn(
            CARD,
            !disabled && "cursor-pointer",
            disabled && "cursor-not-allowed"
          )}
          onClick={() => !disabled && setModalOpen(true)}
        >
          <div className="p-6 sm:p-8">
            {/* Creative Compact Dashboard Layout */}
            <div className="space-y-6">
              {/* Top Row: Order Status + Overall Progress - Side by Side */}
              <div className="flex items-center justify-between pb-6 border-b border-border/20">
                <div className="flex items-center gap-3">
                  <div>
                    <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide mb-1">
                      {uiLabels.orderStatus}
                    </div>
                    <div className="text-lg font-semibold text-foreground">
                      {translatedStatus || "—"}
                    </div>
                    {deliveryStatus && (
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {translatedDeliveryStatus}
                      </div>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide mb-1">
                    {uiLabels.overallProgress}
                  </div>
                  <div className="text-2xl font-bold text-foreground">
                    {overallProgress}%
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {uiLabels.averageCompletion}
                  </div>
                </div>
              </div>

              {/* Fulfillment Pipeline: Three Statuses in Horizontal Flow */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {/* Delivered - Compact Card */}
                <div className="relative rounded px-5 py-5 bg-[rgb(247_249_253_/_91%)] dark:bg-gray-800/50 border border-border/20">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide mb-1">
                        {uiLabels.delivered}
                      </div>
                      <div className="text-xs text-foreground/70 font-medium">
                        {translatedDeliveryStatus || uiLabels.notStarted}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-foreground">
                        {fmtPercent(perDelivered)}%
                      </div>
                    </div>
                  </div>
                  {/* Compact Progress Bar - Subtle blue accent */}
                  <div className="w-full h-1.5 bg-blue-50/30 dark:bg-blue-900/10 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500 bg-gradient-to-r from-blue-400 via-blue-500 to-blue-600 dark:from-blue-500 dark:via-blue-600 dark:to-blue-700"
                      style={{ width: `${Math.min(perDelivered, 100)}%` }}
                    />
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-2 font-normal">
                    {getProgressLabel(perDelivered, "delivered")}
                  </div>
                </div>

                {/* Billed - Compact Card */}
                <div className="relative rounded px-5 py-5 bg-[rgb(247_249_253_/_91%)] dark:bg-gray-800/50 border border-border/20">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide mb-1">
                        {uiLabels.amountBilled}
                      </div>
                      <div className="text-xs text-foreground/70 font-medium">
                        {perBilled === 100 ? uiLabels.fullyBilled : perBilled === 0 ? uiLabels.notBilled : uiLabels.partial}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-foreground">
                        {fmtPercent(perBilled)}%
                      </div>
                    </div>
                  </div>
                  {/* Compact Progress Bar - Subtle green accent */}
                  <div className="w-full h-1.5 bg-green-50/30 dark:bg-green-900/10 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500 bg-gradient-to-r from-green-400 via-green-500 to-green-600 dark:from-green-500 dark:via-green-600 dark:to-green-700"
                      style={{ width: `${Math.min(perBilled, 100)}%` }}
                    />
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-2 font-normal">
                    {getProgressLabel(perBilled, "billed")}
                  </div>
                </div>

                {/* Picked - Compact Card */}
                <div className="relative rounded px-5 py-5 bg-[rgb(247_249_253_/_91%)] dark:bg-gray-800/50 border border-border/20">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide mb-1">
                        {uiLabels.picked}
                      </div>
                      <div className="text-xs text-foreground/70 font-medium">
                        {perPicked === 100 ? uiLabels.fullyPicked : perPicked === 0 ? uiLabels.notPicked : uiLabels.partial}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-foreground">
                        {fmtPercent(perPicked)}%
                      </div>
                    </div>
                  </div>
                  {/* Compact Progress Bar - Subtle purple accent */}
                  <div className="w-full h-1.5 bg-purple-50/30 dark:bg-purple-900/10 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500 bg-gradient-to-r from-purple-400 via-purple-500 to-purple-600 dark:from-purple-500 dark:via-purple-600 dark:to-purple-700"
                      style={{ width: `${Math.min(perPicked, 100)}%` }}
                    />
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-2 font-normal">
                    {getProgressLabel(perPicked, "picked")}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <MetaFieldEditModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        section={section}
        formData={formData}
        onFieldChange={onFieldChange}
        onSave={onSave}
        disabled={disabled}
        isSaving={isSaving}
      />
    </>
  );
}


